import { liteClient } from "algoliasearch/lite";

import type { IAlgoliaCredentials } from "../interface/algolia";
import { configureInsights, getAlgoliaUserToken } from "./insights";

// 앱은 기존처럼 client 에서 불러 쓴다. 상태는 useTracking 도 함께 쓰는 insights.ts 에 있다.
export { setAlgoliaUserToken } from "./insights";

/**
 * 현재 스토어의 자격 증명. 앱이 진입점에서 configureAlgolia() 로 주입한다.
 *
 * 모듈 로드 시점에 env 를 읽어 두던 방식은 멀티스토어(도메인으로 KR/INTL/JP 를 고르는 앱)에서
 * 주입 전 값이 굳어 버려서 없앴다. 인덱스명이 필요한 곳은 전부 아래 getter 로 호출 시점에 읽는다.
 */
let credentials: IAlgoliaCredentials = {
  appId: "",
  searchKey: "",
  indexName: "",
};

let client: ReturnType<typeof liteClient> | null = null;

/** 스토어별 자격 증명 주입. 앱 진입점(lib/algolia.ts → main.tsx)에서 첫 렌더 전에 한 번 호출한다. */
export function configureAlgolia(next: IAlgoliaCredentials) {
  credentials = next;
  client = null;
  configureInsights(next);
}

/** 현재 스토어의 기본 인덱스명 */
export function getAlgoliaIndex(): string {
  return credentials.indexName;
}

/** 없는 인덱스로 질의하면 404 가 나서 화면이 깨지므로, 비어 있으면 기본 인덱스로 폴백한다. */
export function getAlgoliaReplica(name: "priceAsc" | "priceDesc" | "createdDesc" | "bestSelling"): string {
  const value = credentials.replicas?.[name];
  return value && value.trim() !== "" ? value : credentials.indexName;
}

export function getQuerySuggestionsIndex(): string {
  const value = credentials.querySuggestionsIndex;
  return value && value.trim() !== "" ? value : `${credentials.indexName}_query_suggestions`;
}

/** 어댑터가 실제로 쓰는 속성만 받아 응답 크기를 줄인다. */
export const ALGOLIA_RETRIEVED_ATTRIBUTES = [
  "objectID",
  "id",
  "title",
  "handle",
  "vendor",
  "product_type",
  "tags",
  "image",
  "product_image",
  "price",
  "compare_at_price",
  "variants_min_price",
  "variants_compare_at_price_min",
  "inventory_available",
  "variants_inventory_count",
  "created_at",
  "published_at",
  "options",
  "variant_title",
  "position",
  "sku",
  "meta",
];

interface IAlgoliaRequest {
  indexName?: string;
  params?: { query?: string };
}

/** 네트워크 요청 없이 돌려줄 빈 결과. SearchResults 가 파싱할 수 있는 최소 형태. */
function emptyResult(indexName?: string) {
  return {
    hits: [],
    nbHits: 0,
    nbPages: 0,
    page: 0,
    hitsPerPage: 0,
    processingTimeMS: 0,
    exhaustiveNbHits: true,
    facets: {},
    query: "",
    params: "",
    index: indexName ?? getAlgoliaIndex(),
  };
}

function getClient() {
  if (!credentials.appId) {
    // eslint-disable-next-line no-console
    console.warn("[ALGOLIA] configureAlgolia() 가 호출되기 전에 검색했습니다. 앱 진입점에서 lib/algolia 를 불러오세요.");
  }
  if (!client) client = liteClient(credentials.appId, credentials.searchKey);
  return client;
}

/**
 * 검색어가 비어 있으면 Algolia 를 호출하지 않는다.
 * 헤더 검색을 열기만 해도, /search 에 q 없이 들어와도 InstantSearch 가 빈 쿼리로 한 번 검색하기 때문.
 * (Algolia 로 검색어 없이 브라우징하는 화면은 없다 — 컬렉션은 Shopify 쿼리를 쓴다.)
 */

export const searchClient = {
  search(requests: IAlgoliaRequest[], ...rest: unknown[]) {
    const isEmpty = requests.every((request) => !request.params?.query?.trim());

    if (isEmpty) {
      return Promise.resolve({
        results: requests.map((request) => emptyResult(request.indexName)),
      });
    }

    // 로그인 고객이면 userToken 을 싣고 clickAnalytics 로 queryID 를 받는다.
    // queryID 는 검색 결과 클릭 · 장바구니 · 구매 이벤트를 이 검색에 연결하는 데 쓴다(insights.ts).
    const userToken = getAlgoliaUserToken();
    const withUserToken = userToken
      ? requests.map((request) => ({ ...request, params: { ...request.params, userToken, clickAnalytics: true } }))
      : requests;

    return (getClient().search as (...args: unknown[]) => Promise<unknown>)(withUserToken, ...rest);
  },
} as unknown as ReturnType<typeof liteClient>;

export default searchClient;
